import { ReactNode, useEffect, useMemo } from "react";
import { createIntl, defineMessages, useIntl } from "react-intl";
import { gql } from "@apollo/client";
import { useMutation } from "@apollo/client/react";
import { IconChevronRight } from "@tabler/icons-react";
import { classNames, useAppConfig } from "@ct-react/core";
import { defaultLocale, libTranslate, useLocaleContext, useLocaleFormatter } from "@ct-react/locale";
import { Location, MailContact, resumeLocation } from "@shared/graphql";
import { getIntlMessages } from "../i18n";
import { actions, form } from "../i18n/sharable-defs";
import { useTools } from "../hooks/tools";
import { useForm, validators } from "../hooks/form";
import { Wrapper } from "../components/layout/wrapper";
import SeoMetas from "../components/layout/seo-metas";
import Button from "../components/common/button";
import PageHero from "../components/layout/page-elements/page-hero";
import Select, { Option } from "../components/inputs/select";
import CheckboxInput from "../components/inputs/checkbox";
import Elsje from "../components/common/elsje";
import hero from "../assets/bg-images/hero-estimation.jpg";
import "./estimate.scss";

enum Condition { NEW = "new", EXCELLENT = "excellent", GOOD = "good", AVERAGE = "average", BAD = "bad" }

const transDefs = defineMessages({
  title: { id: "estimation.title", defaultMessage: "Estimation" },
  chapo: { id: "estimation.chapo", defaultMessage: "Que vaut votre bien sur le marché de l'immobilier ?" },
  lead: { id: "estimation.lead-txt", defaultMessage: "EV Real Estate & Services mettra à profit son expertise et sa connaissance du secteur des 4 vallées pour estimer votre bien en fonction du marché actuel. Remplissez notre formulaire d’estimation en ligne et recevez une estimation personnalisée et gratuite de votre bien. Nous serons ravis de vous guider dans les démarches de mise en vente de votre propriété." },
  construction: { id: "estimation.form.construction-year", defaultMessage: "Année de construction" },
  renovation: { id: "estimation.form.renovation-year", defaultMessage: "Année de rénovation" },
  condition: {id: "estimation.form.condition", defaultMessage: "État du bien" },
  conditionPlaceholder: {id: "estimation.form.condition-placeholder", defaultMessage: "Sélectionnez l'état du bien" },
  address: { id: "estimation.form.address", defaultMessage: "Adresse du bien" },
  surface: { id: "estimation.form.surface", defaultMessage: "Superficie" },
  rooms: { id: "estimation.form.rooms", defaultMessage: "Nombre de pièces" },
  bedrooms: { id: "estimation.form.bedrooms", defaultMessage: "Nombre de chambres" },
  bathrooms: { id: "estimation.form.bathrooms", defaultMessage: "Nombre de salles de bains" },
  secondary: { id: "estimation.form.secondary", defaultMessage: "Vente possible en résidence secondaire" },
  foreigners: { id: "estimation.form.foreigners", defaultMessage: "Vente possible aux non-résidents" },
  success: { id: "estimation.form.success", defaultMessage: "Votre demande a été prise en compte avec succès" }
});

const conditionsDefs = defineMessages<Condition>({
  [Condition.NEW]: { id: "estimation.form.condition-new", defaultMessage: "Neuf" },
  [Condition.EXCELLENT]: { id: "estimation.form.condition-excellent", defaultMessage: "Excellent" },
  [Condition.GOOD]: { id: "estimation.form.condition-good", defaultMessage: "Bon" },
  [Condition.AVERAGE]: { id: "estimation.form.condition-average", defaultMessage: "Moyen" },
  [Condition.BAD]: { id: "estimation.form.condition-bad", defaultMessage: "Mauvais" },
});

const SEND_MAIL_TO_AGENCY_GQL_MUT = gql`
  mutation MailEstimation($agencyId: ID!, $request: MailRequestInput!) {
    mailRelatedContact(merchantId: $agencyId, request: $request) @recaptcha(action: "mail_form")
  }
`;

const allowedObjectTypes = [ 37, 4, 43, 93, 48 ];

type FormData = {
  contact: MailContact;
  data: {
    objectType: number | "any";
    constructionYear?: number;
    renovationYear?: number;
    condition?: Condition;
    address: Location;
    surface?: number;
    rooms?: number;
    bedrooms?: number;
    bathrooms?: number;
    canBeSecondaryHome?: boolean;
    isSellableToForeigners?: boolean
  }
  legal: boolean;
}

const EstimatePage = () => {

  const intl = useIntl();
  const { options: { agencyId } } = useAppConfig();
  const { transLib } = useLocaleFormatter();
  const { locale: { basename } } = useLocaleContext();

  const { countries, mutation } = useTools();
  const policyPath = useMemo(() => `/conditions/policy-${basename.toLowerCase()}.pdf`, [ basename ]);

  const { register, formState, patch, handleSubmit } = useForm<FormData>({ data: { address: { country: "CH" } }, legal: false });
  const [ sendMail ] = useMutation(SEND_MAIL_TO_AGENCY_GQL_MUT);

  const onSubmit = async (data: Partial<FormData>) => {
    const fr = createIntl({ locale: defaultLocale.code, messages: getIntlMessages(defaultLocale) });
    const objectType = data.data!.objectType === "any" ? "indéfini" : libTranslate(fr, "objectTypes", +data.data!.objectType);
    const address = resumeLocation(data.data!.address);
    const construction = data.data!.constructionYear || "indéfini";
    const renovation = data.data!.renovationYear || "indéfini";
    const condition = !!data.data?.condition ? fr.formatMessage(conditionsDefs[data.data!.condition as Condition]) : "indéfini";
    const surface = !!data.data?.surface && +data.data.surface > 0 ? `${+data.data.surface} m²` : "indéfini";
    const rooms = !!data.data?.rooms && +data.data.rooms > 0 ? data.data.rooms : "indéfini";
    const bedrooms = !!data.data?.bedrooms && +data.data.bedrooms > 0 ? data.data.bedrooms : "indéfini";
    const bathrooms = !!data.data?.bathrooms && +data.data.bathrooms > 0 ? data.data.bathrooms : "indéfini";
    const secondary = !!data.data!.canBeSecondaryHome ? "oui" : "non";
    const foreigners = !!data.data!.isSellableToForeigners ? "oui" : "non";
    const variables = {
      agencyId,
      request: {
        contact: data.contact,
        subject: "Demande d'estimation",
        body: `Adresse de contact: ${address}\n\nCritères:\n\nType de bien: ${objectType}\nAnnée de construction: ${construction}\nAnnée de rénovation: ${renovation}\nÉtat du bien: ${condition}\nSuperficie: ${surface}\nNombre de pièces: ${rooms}\nNombre de chambres: ${bedrooms}\nNombre de salles de bains: ${bathrooms}\nVendable en résidence secondaire: ${secondary}\nVendable aux non-résidents: ${foreigners}`
      }
    }
    await mutation.withRecaptcha(sendMail, "mail_form")({ variables })
                  .then(() => patch(true));
  }

  return (
    <>
      <SeoMetas title={`${intl.formatMessage(transDefs.chapo)} | EV Real Estate`} />

      <PageHero background={hero}
                title={intl.formatMessage(transDefs.title)}
                chapo={intl.formatMessage(transDefs.chapo)} />

      <div className="estimation lead">
        <Wrapper.Centered className="content">
          <p>{intl.formatMessage(transDefs.lead)}</p>
        </Wrapper.Centered>
      </div>

      <Wrapper.Centered className="content estimation">
        <main>
          <form className="estimation-form"
                onSubmit={handleSubmit(onSubmit, { resetStateSubmitTimer: 5000 })}>

            <div className="f-group">
              <label htmlFor="firstName">{`${intl.formatMessage(form.firstName)}*`}</label>
              <input type="text" id="firstName"
                     className={classNames({ dirty: "contact.firstName" in formState.dirtyFields })}
                     {...register("contact.firstName", { required: intl.formatMessage(form.emptyError) })}
                     placeholder={intl.formatMessage(form.firstName)}
                     autoComplete="given-name" />
              <p className="f-error">
                {!!formState.dirtyFields["contact.firstName"] && formState.dirtyFields["contact.firstName"].error}
              </p>
            </div>

            <div className="f-group">
              <label htmlFor="lastName">{`${intl.formatMessage(form.lastName)}*`}</label>
              <input type="text" id="lastName"
                     className={classNames({ dirty: "contact.lastName" in formState.dirtyFields })}
                     {...register("contact.lastName", { required: intl.formatMessage(form.emptyError) })}
                     placeholder={intl.formatMessage(form.lastName)}
                     autoComplete="family-name" />
              <p className="f-error">
                {!!formState.dirtyFields["contact.lastName"] && formState.dirtyFields["contact.lastName"].error}
              </p>
            </div>

            <div className="f-group">
              <label htmlFor="email">{`${intl.formatMessage(form.email)}*`}</label>
              <input type="email" id="email"
                     className={classNames({ dirty: "contact.email" in formState.dirtyFields })}
                     {...register("contact.email", {
                       required: intl.formatMessage(form.emptyError),
                       validators: { ...validators.emailFormat(intl.formatMessage(form.emailError)) }
                     })}
                     placeholder={intl.formatMessage(form.email)}
                     autoComplete="email" />
              <p className="f-error">
                {!!formState.dirtyFields["contact.email"] && formState.dirtyFields["contact.email"].error}
              </p>
            </div>

            <div className="f-group">
              <label htmlFor="phone">{`${intl.formatMessage(form.phone)}*`}</label>
              <input type="tel" id="phone"
                     className={classNames({ dirty: "contact.phone" in formState.dirtyFields })}
                     {...register("contact.phone", {
                       required: intl.formatMessage(form.emptyError),
                       validators: { ...validators.phoneFormat(intl.formatMessage(form.phoneError)) }
                     })}
                     placeholder={intl.formatMessage(form.phone)}
                     autoComplete="tel" />
              <p className="f-error">
                {!!formState.dirtyFields["contact.phone"] && formState.dirtyFields["contact.phone"].error}
              </p>
            </div>

            <div className="sub">

              <div className="f-group">
                <label>{`${intl.formatMessage(form.objectType)}*`}</label>
                <Select className={classNames({ dirty: "data.objectType" in formState.dirtyFields })}
                        placeholder={intl.formatMessage(form.objectTypePlaceholder)}
                        {...register("data.objectType", { required: intl.formatMessage(form.emptyError) })}>
                  {[
                    ...allowedObjectTypes.map(id =>
                      <Option key={id} value={id} label={transLib.match("objectTypes", id)} />),
                    <Option key="any" value="any" label={intl.formatMessage(form.other)} />
                  ]}
                </Select>
                <p className="f-error">
                  {!!formState.dirtyFields["data.objectType"] && formState.dirtyFields["data.objectType"].error}
                </p>
              </div>

              <div className="f-group no-error">
                <label htmlFor="rooms">{intl.formatMessage(transDefs.construction)}</label>
                <input id="rooms"
                       type="text" pattern="\d{4}" placeholder="YYYY" maxLength={4}
                       {...register("data.constructionYear")} />
              </div>

              <div className="f-group no-error">
                <label htmlFor="rooms">{intl.formatMessage(transDefs.renovation)}</label>
                <input id="rooms"
                       type="text" pattern="\d{4}" placeholder="YYYY" maxLength={4}
                       {...register("data.renovationYear")} />
              </div>

              <div className="f-group no-error">
                <label>{intl.formatMessage(transDefs.condition)}</label>
                <Select className={classNames({ dirty: "data.condition" in formState.dirtyFields })}
                        placeholder={intl.formatMessage(transDefs.conditionPlaceholder)}
                        {...register("data.condition")}>
                  {Object.values(Condition).map((value, index) =>
                    <Option key={index} value={value} label={intl.formatMessage(conditionsDefs[value as Condition])} />
                  )}
                </Select>
              </div>

            </div>

            <div className="sub address">

              <div className="f-group">
                <label htmlFor="address">{`${intl.formatMessage(transDefs.address)}*`}</label>
                <div className="fl-group">
                  <input type="text" id="address"
                         className={classNames({ dirty: "data.address.street" in formState.dirtyFields })}
                         {...register("data.address.street", {
                           required: intl.formatMessage(form.emptyError)
                         })}
                         placeholder={intl.formatMessage(form.street)}
                         autoComplete="street-address" />
                  <input type="text"
                         className={classNames("street-min", { dirty: "data.address.streetNumber" in formState.dirtyFields })}
                         {...register("data.address.streetNumber")}
                         placeholder={intl.formatMessage(form.streetNumber)} />
                </div>
                <p className="f-error">
                  {!!formState.dirtyFields["data.address.street"] && formState.dirtyFields["data.address.street"].error}
                </p>
              </div>

              <div className="f-group no-label no-error">
                <input type="text" id="address-complement"
                       className={classNames({ dirty: "data.address.streetComplement" in formState.dirtyFields })}
                       {...register("data.address.streetComplement")}
                       placeholder={intl.formatMessage(form.streetComplement)} />
              </div>

              <div className="f-group no-label">
                <div className="fl-group">
                  <input type="text"
                         className={classNames("zip-min", { dirty: "data.address.zip" in formState.dirtyFields })}
                         {...register("data.address.zip", {
                           required: intl.formatMessage(form.emptyError)
                         })}
                         placeholder={intl.formatMessage(form.zip)}
                         autoComplete="postal-code" />
                  <input type="text"
                         {...register("data.address.city", {
                           required: intl.formatMessage(form.emptyError)
                         })}
                         placeholder={intl.formatMessage(form.city)} />
                </div>
                <p className="f-error">
                  {(!!formState.dirtyFields["data.address.zip"] || !!formState.dirtyFields["data.address.city"]) &&
                    (formState.dirtyFields["data.address.zip"] || formState.dirtyFields["data.address.city"]).error
                  }
                </p>
              </div>

              <div className="f-group no-label">
                <Select className={classNames({ dirty: "data.address.country" in formState.dirtyFields })}
                        {...register("data.address.country", {
                          required: intl.formatMessage(form.emptyError)
                        })}
                        placeholder={intl.formatMessage(form.countryPlaceholder)}>
                  {Object.entries(countries).map(([ value, label ], index) => (
                    <Option key={index} value={value} label={label} />
                  ))}
                </Select>
                <p className="f-error">
                  {!!formState.dirtyFields["data.address.country"] && formState.dirtyFields["data.address.country"].error}
                </p>
              </div>

            </div>

            <div className="f-group no-error">
              <label htmlFor="rooms">{intl.formatMessage(transDefs.surface)}</label>
              <input type="number" id="rooms" {...register("data.surface")} min={0} />
            </div>

            <div className="f-group no-error">
              <label htmlFor="rooms">{intl.formatMessage(transDefs.rooms)}</label>
              <input type="number" id="rooms" {...register("data.rooms")} min={0} />
            </div>

            <div className="f-group no-error">
              <label htmlFor="rooms">{intl.formatMessage(transDefs.bedrooms)}</label>
              <input type="number" id="rooms" {...register("data.bedrooms")} min={0} />
            </div>

            <div className="f-group no-error">
              <label htmlFor="rooms">{intl.formatMessage(transDefs.bathrooms)}</label>
              <input type="number" id="rooms" {...register("data.bathrooms")} min={0} />
            </div>

            <div className="f-group no-error">
              {/*@ts-ignore*/}
              <CheckboxInput id="secondary"
                             label={intl.formatMessage(transDefs.secondary)}
                             {...register<boolean>("data.canBeSecondaryHome")} />
            </div>

            <div className="f-group no-error">
              {/*@ts-ignore*/}
              <CheckboxInput id="foreigner"
                             label={intl.formatMessage(transDefs.foreigners)}
                             {...register<boolean>("data.isSellableToForeigners")} />
            </div>

            <div className="full legal f-group no-error">
              {/*@ts-ignore*/}
              <CheckboxInput id="legal"
                             label={intl.formatMessage(form.legal, { link: (chunks: ReactNode) =>
                                 (<a key="link" href={policyPath} target="_blank">{chunks}</a>)}
                             )}
                             {...register<boolean>("legal", { required: intl.formatMessage(form.emptyError) })} />
            </div>

            <div className="full submitter">
              <Button type="submit" className="cream bold l-spacing with-icon"
                      processing={formState.submit.inProcess}
                      disabled={!formState.isValid}>
                {intl.formatMessage(actions.send)}
                <IconChevronRight />
              </Button>
              {formState.submit.finished &&
                <span className={classNames("callback", { error: !formState.submit.succeeded })}>
                  {intl.formatMessage(formState.submit.succeeded ? transDefs.success : form.failed)}
                </span>
              }
            </div>

          </form>
        </main>
        <aside><Elsje /></aside>
      </Wrapper.Centered>
    </>);
};

export default EstimatePage;
